import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { useDisconnectQuickbooks } from "@/business/quickbooks-query";
import { getErrorMessage } from "@/core/api/errors";

/** Confirms a disconnect, which keeps settings and vendor links for later. */
export const QuickbooksDisconnectDialog = ({
  businessId,
  open,
  onClose,
}: {
  businessId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { mutate: disconnect, isPending } = useDisconnectQuickbooks(businessId);

  const handleConfirm = () =>
    disconnect(undefined, {
      onSuccess: () => {
        toast.success("QuickBooks disconnected");
        onClose();
      },
      onError: (error) =>
        toast.error("Couldn’t disconnect QuickBooks", {
          description: getErrorMessage(error),
        }),
    });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isPending) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Disconnect QuickBooks?</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm disconnecting QuickBooks
          </DialogDescription>
        </DialogHeader>
        <Stack align="start" className="w-full" gap={4}>
          <Text tone="muted">
            Volca will stop creating bills in QuickBooks.
          </Text>
          <Text tone="muted">
            Your bill settings and vendor links are kept. Reconnect the same
            QuickBooks company to pick up where you left off.
          </Text>
        </Stack>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleConfirm}
            disabled={isPending}
            loading={isPending}
          >
            Disconnect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
